<script lang="ts">
	//Hooks
	import { createEventDispatcher } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Helpers
	import { sleepingResponse } from '../../../helpers/device_settings_helpers';
	//Design system components
	import { Modal, Button, Text, Alert, Loader, ListItem, Stack } from '@Shelly/shelly-design-system';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';

	export let device: Device;
	export let isOffline;
	export let enable = true;

	const defaultMaxShowed = 9;
	let maxShowedNetworks = defaultMaxShowed;
	let showMore = false;
	let showModal = false,
		waitForResponse = false,
		networksListError = '',
		availableNetworks = null;

	const dispatch = createEventDispatcher(),
		getAvailableNetworks = async () => {
			waitForResponse = true;
			const res: SettingRequest = await gen2Request(device, 'WiFi.Scan', {}, false),
				networksList = [];
			waitForResponse = false;
			if (res.isok) {
				//@ts-ignore
				const results = res?.data?.results || res?.results || [],
					checkDuplicateList = [];
				results.forEach((network) => {
					if (checkDuplicateList.indexOf(network.ssid) == -1) {
						const icon = `fa-wifi${network.rssi < -67 ? (network.rssi < -80 ? '-weak' : '-fair') : ''}`;
						networksList.push({
							value: network.ssid,
							title: network.ssid + (network.auth == 0 ? ' (open)' : ' '),
							is_open: network.auth == 0,
							icon,
						});
					}
					checkDuplicateList.push(network.ssid);
				});
			}
			if (!networksList?.length) {
				networksListError =
					sleepingResponse(res, device.type) ||
					$format('available_networks_error', {
						default: 'Could not load available networks.',
					});
			}
			return networksList;
		};

	const resetShowMore = () => {
		showMore = false;
		maxShowedNetworks = defaultMaxShowed;
	};

	const onSelect = (networkItem) => {
		showModal = false;
		resetShowMore();
		dispatch('select', networkItem);
	};
</script>

<Button
	disabled={!enable || isOffline}
	size="m"
	width="full"
	variant="secondary"
	class="mb-s"
	on:click={() => {
		showModal = true;
		availableNetworks = getAvailableNetworks();
	}}
	loading={waitForResponse}
>
	{$format('scan_for_wifi_networks', {
		default: 'Scan for Wi-Fi networks',
	})}
</Button>

<div class="modal">
	{#if showModal}
		<Modal
			on:close={() => {
				showModal = false;
				resetShowMore();
			}}
			{showModal}
			backdropClose={true}
			title={$format('available_networks', { default: 'Available networks' })}
		>
			<div slot="content">
				{#await availableNetworks}
					<div class="d-flex justify-content-center align-items-center flex-direction-column">
						<Loader class="mb-xs" />
						<Text styleType="controls-small" class="text-center text-bold">
							{$format('title_scanning', {
								default: 'Scanning ...',
							})}
						</Text>
					</div>
				{:then networksList}
					{#if networksList?.length}
						<Stack gap="s">
							{#each networksList as networkItem, i}
								{#if i < maxShowedNetworks}
									<ListItem
										viewType="fullColored"
										listItemBgColor="--color-1"
										icon={'fa-duotone ' + networkItem.icon}
										actionBtn
										title={networkItem.title}
										on:click={() => onSelect(networkItem)}
									/>
								{/if}
							{/each}

							{#if !showMore}
								<ListItem
									title={$format('show_more_network_items', {
										values: {
											networkItems: networksList.length - maxShowedNetworks,
										},
										default: `+${networksList.length - maxShowedNetworks} ${$format(
											'more',
											{
												default: 'more',
											},
										)}`,
									})}
									viewType="more"
									listItemBgColor="--color-1"
									on:click={() => {
										showMore = true;
										maxShowedNetworks = networksList.length;
									}}
								/>
							{/if}
						</Stack>
					{:else}
						<Alert type="danger" heading={networksListError} class="mb-s" />
						<Button
							size="m"
							class="width-full mr-s text-capitalized"
							responsiveSize={{
								sm: 'm',
							}}
							variant="primary"
							on:click={() => {
								availableNetworks = getAvailableNetworks();
							}}
						>
							{$format('button_rescan', { default: 'Rescan' })}
						</Button>
					{/if}
				{/await}
			</div>
		</Modal>
	{/if}
</div>
